import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Chip,
  Tooltip,
  useMediaQuery,
  Stack,
  Pagination,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import styles from "./Certifications.module.css";

// Max users returned per search in the Add Volunteer dialog.
const USER_SEARCH_LIMIT = 100;

const CertificationEdit = () => {
  const { certTypeId } = useParams();
  const isCreate = certTypeId === undefined || certTypeId === null;
  const certTypeIdNum = isCreate ? null : Number(certTypeId);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const [certType, setCertType] = useState(null);
  const [volunteers, setVolunteers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [isSaving, setIsSaving] = useState(false);
  const [draft, setDraft] = useState({ Name: "", Description: "" });

  // Add volunteer dialog state
  const [isAddVolunteerOpen, setIsAddVolunteerOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [isUserSearching, setIsUserSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [expirationDate, setExpirationDate] = useState("");
  const [notes, setNotes] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);

  // Edit volunteer dialog state
  const [isEditVolunteerOpen, setIsEditVolunteerOpen] = useState(false);
  const [editingVolunteer, setEditingVolunteer] = useState(null);
  const [editExpiration, setEditExpiration] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete volunteer dialog state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, volunteer: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState([]);

  const formatApiError = (e) =>
    e?.response?.data?.error ||
    (typeof e?.response?.data === "string" ? e.response.data : null) ||
    e?.message ||
    "Unknown error";

  const formatDate = (dateStr) => {
    if (!dateStr) return "No expiration";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "Invalid date";
    // Certification expirations are stored as UTC-midnight dates (Dec 31 of the
    // year). Render in UTC so a browser west of UTC doesn't shift it back a day.
    return d.toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric", timeZone: "UTC"
    });
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    try {
      if (isCreate) {
        setCertType(null);
        setDraft({ Name: "", Description: "" });
        setVolunteers([]);
        return;
      }

      if (!certTypeIdNum || Number.isNaN(certTypeIdNum)) {
        setError(true);
        return;
      }

      // Fetch certification type
      const typeResp = await axiosPrivate.get(`/api/certification/certification-types/${certTypeIdNum}`);
      const ct = typeResp?.data || null;

      if (!ct) {
        setError(true);
        return;
      }

      // Fetch volunteers for this certification type
      const volunteersResp = await axiosPrivate.get(`/api/certification/certification-types/${certTypeIdNum}/volunteers`);
      const vols = Array.isArray(volunteersResp?.data) ? volunteersResp.data : [];

      setCertType(ct);
      setDraft({
        Name: ct?.Name ?? "",
        Description: ct?.Description ?? ""
      });
      setVolunteers(vols);
    } catch (e) {
      setError(true);
      enqueueSnackbar(formatApiError(e) || "Failed to load certification", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [axiosPrivate, enqueueSnackbar, isCreate, certTypeIdNum]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Debounce user search input
  useEffect(() => {
    if (!isAddVolunteerOpen) return;
    const t = setTimeout(() => setDebouncedUserSearch(userSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [userSearch, isAddVolunteerOpen]);

  // Fetch users for selector (search)
  useEffect(() => {
    if (!isAddVolunteerOpen) return;
    let cancelled = false;
    const run = async () => {
      setIsUserSearching(true);
      try {
        const resp = await axiosPrivate.get("/api/users", {
          params: { page: 1, limit: USER_SEARCH_LIMIT, search: debouncedUserSearch, basic: 1 }
        });
        const list = resp?.data?.users || [];
        if (!cancelled) setUserOptions(list);
      } catch (e) {
        if (!cancelled) {
          setUserOptions([]);
          enqueueSnackbar("User search failed", { variant: "error" });
        }
      } finally {
        if (!cancelled) setIsUserSearching(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [axiosPrivate, debouncedUserSearch, enqueueSnackbar, isAddVolunteerOpen]);

  const filteredVolunteers = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    return (volunteers || []).filter((v) => {
      const name = String(v?.FullName ?? "").toLowerCase();
      const email = String(v?.Email ?? "").toLowerCase();
      return name.includes(lower) || email.includes(lower);
    });
  }, [searchTerm, volunteers]);

  // Sort the ENTIRE filtered list (not just the current page) so sorting applies
  // across all records. Pagination slices this sorted list below.
  const sortedVolunteers = useMemo(() => {
    if (!sortModel.length) return filteredVolunteers;
    const { field, sort } = sortModel[0];
    if (!field || !sort) return filteredVolunteers;
    const dir = sort === "desc" ? -1 : 1;
    const keyOf = (row) => {
      if (field === "Expiration") {
        const t = row?.Expiration ? new Date(row.Expiration).getTime() : NaN;
        return Number.isNaN(t) ? -Infinity : t; // blank/"No expiration" sorts first asc
      }
      return String(row?.[field] ?? "").toLowerCase();
    };
    return [...filteredVolunteers].sort((a, b) => {
      const av = keyOf(a), bv = keyOf(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filteredVolunteers, sortModel]);

  const handleSaveCertType = async () => {
    const payload = {
      Name: String(draft.Name ?? "").trim(),
      Description: String(draft.Description ?? "").trim()
    };
    if (!payload.Name) {
      enqueueSnackbar("Certification name is required", { variant: "warning" });
      return;
    }

    setIsSaving(true);
    try {
      if (isCreate) {
        const created = await axiosPrivate.post(`/api/certification/certification-types`, payload);
        const newId = created?.data?.CertificationTypeID;
        enqueueSnackbar("Certification type created", { variant: "success" });
        if (newId) {
          navigate(`/admin/certifications/edit/${newId}`, { replace: true });
          return;
        }
      } else {
        await axiosPrivate.put(`/api/certification/certification-types/${certTypeIdNum}`, payload);
        enqueueSnackbar("Certification type updated", { variant: "success" });
      }
      await fetchAll();
    } catch (e) {
      enqueueSnackbar(formatApiError(e) || "Failed to save certification type", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  // Add volunteer handlers
  const openAddVolunteer = () => {
    setSelectedUser(null);
    setUserSearch("");
    setDebouncedUserSearch("");
    setUserOptions([]);
    setExpirationDate("");
    setNotes("");
    setIsAddVolunteerOpen(true);
  };

  const closeAddVolunteer = () => {
    if (isAssigning) return;
    setIsAddVolunteerOpen(false);
  };

  const getUserLabel = (u) => {
    if (!u) return "";
    const full = String(u.FullName || `${u.FirstName || ""} ${u.LastName || ""}`).trim();
    return full || u.UserName || String(u.UserID || "");
  };

  const handleAssignVolunteer = async () => {
    if (isCreate) {
      enqueueSnackbar("Create the certification type first, then add volunteers.", { variant: "info" });
      return;
    }
    if (!selectedUser?.UserGUID) {
      enqueueSnackbar("Please select a user", { variant: "warning" });
      return;
    }
    setIsAssigning(true);
    try {
      await axiosPrivate.post(`/api/certification/certification-types/${certTypeIdNum}/volunteers`, {
        userGuid: selectedUser.UserGUID,
        expiration: expirationDate || null,
        notes: notes || null
      });
      enqueueSnackbar("Volunteer certified successfully", { variant: "success" });
      setIsAddVolunteerOpen(false);
      await fetchAll();
    } catch (e) {
      enqueueSnackbar(formatApiError(e) || "Failed to add certified volunteer", { variant: "error" });
    } finally {
      setIsAssigning(false);
    }
  };

  // Edit volunteer handlers
  const openEditVolunteer = (volunteer) => {
    setEditingVolunteer(volunteer);
    setEditExpiration(formatDateForInput(volunteer?.Expiration));
    setEditNotes(volunteer?.Notes || "");
    setIsEditVolunteerOpen(true);
  };

  const closeEditVolunteer = () => {
    if (isUpdating) return;
    setIsEditVolunteerOpen(false);
    setEditingVolunteer(null);
  };

  const handleUpdateVolunteer = async () => {
    if (!editingVolunteer?.CertificationID) return;
    setIsUpdating(true);
    try {
      await axiosPrivate.put(
        `/api/certification/certification-types/${certTypeIdNum}/volunteers/${editingVolunteer.CertificationID}`,
        { expiration: editExpiration || null, notes: editNotes || null }
      );
      enqueueSnackbar("Certification updated", { variant: "success" });
      setIsEditVolunteerOpen(false);
      await fetchAll();
    } catch (e) {
      enqueueSnackbar(formatApiError(e) || "Failed to update certification", { variant: "error" });
    } finally {
      setIsUpdating(false);
    }
  };

  // Delete volunteer handlers
  const openDeleteDialog = (volunteer) => setDeleteDialog({ open: true, volunteer });
  const closeDeleteDialog = () => {
    if (isDeleting) return;
    setDeleteDialog({ open: false, volunteer: null });
  };

  const handleConfirmDelete = async () => {
    const certId = deleteDialog?.volunteer?.CertificationID;
    if (!certId) return;
    setIsDeleting(true);
    try {
      await axiosPrivate.delete(`/api/certification/certification-types/${certTypeIdNum}/volunteers/${certId}`);
      enqueueSnackbar("Certification removed", { variant: "success" });
      setDeleteDialog({ open: false, volunteer: null });
      await fetchAll();
    } catch (e) {
      enqueueSnackbar(formatApiError(e) || "Failed to remove certification", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        getActions: (params) => [
          <Tooltip key="edit" title="Edit Volunteer" arrow placement="top">
            <span>
              <GridActionsCellItem
                icon={<EditIcon color="error" />}
                label="Edit Volunteer"
                showInMenu={false}
                onClick={() => openEditVolunteer(params.row)}
              />
            </span>
          </Tooltip>,
          <Tooltip key="remove" title="Remove Volunteer" arrow placement="top">
            <span>
              <GridActionsCellItem
                icon={<DeleteIcon color="error" />}
                label="Remove Volunteer"
                showInMenu={false}
                onClick={() => openDeleteDialog(params.row)}
              />
            </span>
          </Tooltip>,
        ]
      },
      { field: "FullName", headerName: "Name", minWidth: 180, flex: 0.8 },
      { field: "Email", headerName: "Email", minWidth: 220, flex: 1 },
      {
        field: "Expiration",
        headerName: "Expiration",
        minWidth: 130,
        flex: 0.5,
        renderCell: (params) => formatDate(params.value)
      },
      {
        field: "Status",
        headerName: "Status",
        minWidth: 100,
        flex: 0.3,
        renderCell: (params) => (
          <Chip
            label={params.value}
            size="small"
            color={params.value === "Active" ? "success" : "error"}
          />
        )
      },
      { field: "Notes", headerName: "Notes", minWidth: 200, flex: 1 }
    ],
    []
  );

  // Loading state
  if (isLoading) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">
                  {isCreate ? "New Certification" : "Edit Certification"}
                </h1>
                <p>Manage certification type and certified volunteers</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <Box className={styles.loadingContainer}>
              <CircularProgress />
              <Typography className={styles.loadingText}>Loading certification...</Typography>
            </Box>
          </div>
        </div>
      </main>
    );
  }

  // Error state
  if (error) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Edit Certification</h1>
                <p>Manage certification type and certified volunteers</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <Box className={styles.errorContainer}>
              <Typography color="error">Error occurred while loading certification. Please contact an admin.</Typography>
              <Button
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={() => navigate("/admin/certifications")}
                sx={{ mt: 2 }}
              >
                Back to Certifications
              </Button>
            </Box>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      {/* Blue Header Banner */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">
                {isCreate ? "New Certification" : `Edit: ${certType?.Name || "Certification"}`}
              </h1>
              <p>Manage certification type and certified volunteers</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="full-row white">
        <div className="">
          <Box className={styles.container}>
            {/* Certification Type Form */}
            <Paper className={styles.searchPaper}>
              <Box className={styles.searchContainer}>
                <Box className={styles.buttonContainer}>
                  <Button
                    variant="outlined"
                    startIcon={<BackIcon />}
                    onClick={() => navigate("/admin/certifications")}
                  >
                    Back
                  </Button>
                  {!isCreate && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openAddVolunteer}
                      className={styles.addButton}
                    >
                      Add Certified Volunteer
                    </Button>
                  )}
                </Box>

                <TextField
                  label="Certification Name"
                  placeholder="e.g., Chainsaw, First Aid"
                  value={draft.Name}
                  onChange={(e) => setDraft((p) => ({ ...p, Name: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <TextField
                  label="Description"
                  placeholder="Description of this certification"
                  value={draft.Description}
                  onChange={(e) => setDraft((p) => ({ ...p, Description: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveCertType}
                    disabled={isSaving}
                    className={styles.confirmButton}
                  >
                    {isSaving ? "Saving..." : isCreate ? "Create Certification" : "Save Changes"}
                  </Button>
                </Box>

                {!isCreate && (
                  <TextField
                    className={styles.searchField}
                    label="Search volunteers"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              </Box>
            </Paper>

            {/* Volunteers Table */}
            {!isCreate && (
              <Paper className={styles.dataGridPaper}>
                <Box sx={{ p: 2, borderBottom: "1px solid #eee", display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h6">
                    Certified Volunteers ({volunteers.length})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
                    <Chip
                      label={`Active: ${volunteers.filter(v => v.Status === 'Active').length}`}
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Expired: ${volunteers.filter(v => v.Status !== 'Active').length}`}
                      color="error"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
                <DataGrid
                  className="certificationsDataGrid"
                  rows={sortedVolunteers.slice(
                    paginationModel.page * paginationModel.pageSize,
                    (paginationModel.page + 1) * paginationModel.pageSize
                  )}
                  columns={columns}
                  getRowId={(row) => row.CertificationID}
                  disableRowSelectionOnClick
                  autoHeight
                  hideFooterPagination
                  sortingMode="server"
                  sortModel={sortModel}
                  onSortModelChange={(m) => {
                    setSortModel(m);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                />
              </Paper>
            )}

            {certType && (
              <Paper className={styles.paginationPaper}>
                <Stack
                  className={styles.paginationStack}
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing {filteredVolunteers.length > 0 ? (paginationModel.page * paginationModel.pageSize) + 1 : 0} to{' '}
                    {Math.min((paginationModel.page + 1) * paginationModel.pageSize, filteredVolunteers.length)} of {filteredVolunteers.length} volunteers
                  </Typography>

                  <Stack className={styles.paginationControls} direction="row" alignItems="center">
                    <Pagination
                      count={Math.ceil(filteredVolunteers.length / paginationModel.pageSize) || 1}
                      page={paginationModel.page + 1}
                      onChange={(_, page) => setPaginationModel((prev) => ({ ...prev, page: page - 1 }))}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                      siblingCount={isMobile ? 0 : 1}
                      boundaryCount={1}
                      className="certificationsPagination"
                    />
                  </Stack>

                  <Box className={styles.pageSizeSelector}>
                    <Typography variant="body2" color="text.secondary">Show:</Typography>
                    <TextField
                      select
                      size="small"
                      value={paginationModel.pageSize}
                      onChange={(e) => {
                        const newLimit = parseInt(e.target.value);
                        setPaginationModel((prev) => ({ ...prev, pageSize: newLimit, page: 0 }));
                      }}
                      SelectProps={{ native: true }}
                      className={`${styles.pageSizeField} pageSizeField`}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </TextField>
                    <Typography variant="body2" color="text.secondary">per page</Typography>
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Add Volunteer Dialog */}
            <Dialog open={isAddVolunteerOpen} onClose={closeAddVolunteer} fullWidth maxWidth="sm">
              <DialogTitle className={styles.dialogTitle}>Add Certified Volunteer</DialogTitle>
              <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <Autocomplete
                    options={userOptions}
                    loading={isUserSearching}
                    value={selectedUser}
                    onChange={(_, v) => setSelectedUser(v)}
                    inputValue={userSearch}
                    onInputChange={(_, v) => setUserSearch(v)}
                    getOptionLabel={getUserLabel}
                    isOptionEqualToValue={(option, value) => option.UserID === value.UserID}
                    // The server already performs the search (name, email, etc.).
                    // Disable Autocomplete's built-in client-side filter, which would
                    // otherwise hide any result whose label doesn't contain the typed text.
                    filterOptions={(options) => options}
                    renderOption={(props, option) => (
                      <li {...props} key={option.UserID}>
                        <Box>
                          <Typography variant="body1">{getUserLabel(option)}</Typography>
                          <Typography variant="body2" color="text.secondary">{option.Email}</Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search users"
                        placeholder="Search by name or email"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <TextField
                    type="date"
                    label="Expiration Date (optional)"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave blank for no expiration"
                  />
                  <TextField
                    label="Notes (optional)"
                    placeholder="Add any notes about this certification"
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </DialogContent>
              <DialogActions className={styles.dialogActions}>
                <Button onClick={closeAddVolunteer} disabled={isAssigning} className={styles.cancelButton}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignVolunteer}
                  disabled={isAssigning || !selectedUser}
                  variant="contained"
                  className={styles.confirmButton}
                  startIcon={isAssigning ? <CircularProgress color="inherit" size={16} /> : <AddIcon />}
                >
                  Add Volunteer
                </Button>
              </DialogActions>
            </Dialog>

            {/* Edit Volunteer Dialog */}
            <Dialog open={isEditVolunteerOpen} onClose={closeEditVolunteer} fullWidth maxWidth="sm">
              <DialogTitle className={styles.dialogTitle}>Edit Certification</DialogTitle>
              <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <Typography variant="body1">
                    <strong>Volunteer:</strong> {editingVolunteer?.FullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {editingVolunteer?.Email}
                  </Typography>
                  <TextField
                    type="date"
                    label="Expiration Date"
                    value={editExpiration}
                    onChange={(e) => setEditExpiration(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave blank for no expiration"
                  />
                  <TextField
                    label="Notes"
                    placeholder="Add any notes about this certification"
                    multiline
                    rows={3}
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </DialogContent>
              <DialogActions className={styles.dialogActions}>
                <Button onClick={closeEditVolunteer} disabled={isUpdating} className={styles.cancelButton}>
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateVolunteer}
                  disabled={isUpdating}
                  variant="contained"
                  className={styles.confirmButton}
                >
                  {isUpdating ? "Updating..." : "Update"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onClose={closeDeleteDialog}>
              <DialogTitle className={styles.dialogTitle}>Remove Certification</DialogTitle>
              <DialogContent>
                <Typography className={styles.dialogText}>
                  Are you sure you want to remove the certification for{' '}
                  <strong>{deleteDialog?.volunteer?.FullName ?? 'this volunteer'}</strong>?
                </Typography>
              </DialogContent>
              <DialogActions className={styles.dialogActions}>
                <Button onClick={closeDeleteDialog} disabled={isDeleting} className={styles.cancelButton}>
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  variant="contained"
                  color="error"
                  className={styles.confirmButtonDelete}
                >
                  {isDeleting ? 'Removing...' : 'Remove'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </div>
      </div>
    </main>
  );
};

export default CertificationEdit;